import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';

// Configuration for memory management
const MEMORY_CONFIG = {
  MAX_CACHE_SIZE: 50 * 1024 * 1024, // 50MB
  MAX_ITEMS: 100,
  CLEANUP_INTERVAL: 30000, // 30 seconds
  LOW_MEMORY_THRESHOLD: 0.8, // 80% of max cache size
};

/**
 * Memory-optimized state management hook for large datasets
 * Implements LRU cache with automatic cleanup
 */
export const useMemoryOptimizedState = (initialValue = null) => {
  const [data, setData] = useState(initialValue);
  const cache = useRef(new Map());
  const sizeTracker = useRef(0);
  const accessOrder = useRef([]);
  const cleanupTimer = useRef(null);

  /**
   * Estimate the size of a JavaScript object in bytes
   */
  const estimateSize = useCallback((obj) => {
    if (!obj) return 0;
    try {
      const jsonString = JSON.stringify(obj);
      return new Blob([jsonString]).size;
    } catch (error) {
      console.warn('Failed to estimate object size:', error);
      return 1024; // Default estimate
    }
  }, []);

  /**
   * Evict least recently used items when cache is full
   */
  const evictLRU = useCallback(() => {
    while (
      (cache.current.size > MEMORY_CONFIG.MAX_ITEMS ||
        sizeTracker.current > MEMORY_CONFIG.MAX_CACHE_SIZE) &&
      accessOrder.current.length > 0
    ) {
      const oldestKey = accessOrder.current.shift();
      const item = cache.current.get(oldestKey);

      if (item) {
        // Cleanup blob URLs if present
        if (item.data && typeof item.data === 'string' && item.data.startsWith('blob:')) {
          URL.revokeObjectURL(item.data);
        }

        // If the item has nested media objects, clean them up too
        if (item.data && item.data.medias) {
          item.data.medias.forEach((media) => {
            if (media.url && media.url.startsWith('blob:')) {
              URL.revokeObjectURL(media.url);
            }
          });
        }

        sizeTracker.current -= item.size;
        cache.current.delete(oldestKey);
      }
    }
  }, []);

  /**
   * Get item from cache and update access order
   */
  const getCached = useCallback(
    (key) => {
      const item = cache.current.get(key);

      if (item) {
        // Update access order
        const index = accessOrder.current.indexOf(key);
        if (index > -1) {
          accessOrder.current.splice(index, 1);
        }
        accessOrder.current.push(key);

        return item.data;
      }

      return null;
    },
    []
  );

  /**
   * Set item in cache with size tracking
   */
  const setCached = useCallback(
    (key, value) => {
      if (!key || value === undefined) return;

      // Check if already cached
      if (cache.current.has(key)) {
        return getCached(key);
      }

      const size = estimateSize(value);

      // Check if we need to evict items
      if (sizeTracker.current + size > MEMORY_CONFIG.MAX_CACHE_SIZE) {
        evictLRU();
      }

      // If single item is too large, don't cache it
      if (size > MEMORY_CONFIG.MAX_CACHE_SIZE * 0.5) {
        console.warn(`Item ${key} is too large to cache (${size} bytes)`);
        return value;
      }

      cache.current.set(key, { data: value, size });
      sizeTracker.current += size;
      accessOrder.current.push(key);

      return value;
    },
    [estimateSize, evictLRU, getCached]
  );

  /**
   * Optimized setState that uses caching for large objects
   */
  const setOptimizedData = useCallback(
    (newData) => {
      if (typeof newData === 'function') {
        setData((prevData) => {
          const result = newData(prevData);

          // Cache the result if it's large enough
          const size = estimateSize(result);
          if (size > 10240) { // 10KB threshold
            const cacheKey = `state_${Date.now()}_${Math.random()}`;
            setCached(cacheKey, result);
          }

          return result;
        });
      } else {
        setData(newData);

        // Cache large objects
        const size = estimateSize(newData);
        if (size > 10240) { // 10KB threshold
          const cacheKey = `state_${Date.now()}_${Math.random()}`;
          setCached(cacheKey, newData);
        }
      }
    },
    [estimateSize, setCached]
  );

  /**
   * Preload data into cache for better performance
   */
  const preloadData = useCallback(
    async (items) => {
      if (!items || !Array.isArray(items)) return;

      const promises = items.map(async (item) => {
        if (item.id && !cache.current.has(item.id)) {
          try {
            // If item has a URL, fetch and create blob URL
            if (item.url && !item.url.startsWith('blob:')) {
              const response = await fetch(item.url);
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              const cachedItem = { ...item, url: blobUrl };
              setCached(item.id, cachedItem);
            } else {
              setCached(item.id, item);
            }
          } catch (error) {
            console.warn('Failed to preload item:', item.id, error);
          }
        }
      });

      await Promise.allSettled(promises);
    },
    [setCached]
  );

  /**
   * Clear all cached data
   */
  const clearCache = useCallback(() => {
    // Cleanup all blob URLs
    for (const [, item] of cache.current) {
      if (item.data && typeof item.data === 'string' && item.data.startsWith('blob:')) {
        URL.revokeObjectURL(item.data);
      }

      // Cleanup nested media URLs
      if (item.data && item.data.medias) {
        item.data.medias.forEach((media) => {
          if (media.url && media.url.startsWith('blob:')) {
            URL.revokeObjectURL(media.url);
          }
        });
      }
    }

    cache.current.clear();
    sizeTracker.current = 0;
    accessOrder.current = [];
  }, []);

  /**
   * Get memory usage statistics
   */
  const getMemoryStats = useCallback(() => {
    const usagePercentage = (sizeTracker.current / MEMORY_CONFIG.MAX_CACHE_SIZE) * 100;

    return {
      cacheSize: cache.current.size,
      memoryUsage: sizeTracker.current,
      memoryUsageMB: (sizeTracker.current / (1024 * 1024)).toFixed(2),
      maxMemoryMB: (MEMORY_CONFIG.MAX_CACHE_SIZE / (1024 * 1024)).toFixed(2),
      usagePercentage: usagePercentage.toFixed(1),
      isNearLimit: usagePercentage > MEMORY_CONFIG.LOW_MEMORY_THRESHOLD * 100,
    };
  }, []);

  /**
   * Force cleanup when memory usage is high
   */
  const forceCleanup = useCallback(() => {
    const stats = getMemoryStats();
    if (stats.isNearLimit) {
      // Evict 25% of cached items
      const itemsToEvict = Math.floor(cache.current.size * 0.25);
      for (let i = 0; i < itemsToEvict && accessOrder.current.length > 0; i++) {
        const oldestKey = accessOrder.current.shift();
        const item = cache.current.get(oldestKey);

        if (item) {
          if (item.data && typeof item.data === 'string' && item.data.startsWith('blob:')) {
            URL.revokeObjectURL(item.data);
          }
          sizeTracker.current -= item.size;
          cache.current.delete(oldestKey);
        }
      }

      const newStats = getMemoryStats();
      message.info(`Memory optimized: ${newStats.memoryUsageMB}MB used (${newStats.usagePercentage}%)`);
    }
  }, [getMemoryStats]);

  // Set up periodic cleanup
  useEffect(() => {
    cleanupTimer.current = setInterval(() => {
      const stats = getMemoryStats();
      if (stats.isNearLimit) {
        forceCleanup();
      }
    }, MEMORY_CONFIG.CLEANUP_INTERVAL);

    return () => {
      if (cleanupTimer.current) {
        clearInterval(cleanupTimer.current);
      }
      clearCache();
    };
  }, [getMemoryStats, forceCleanup, clearCache]);

  // Monitor memory performance
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const logMemoryStats = () => {
        const stats = getMemoryStats();
        if (stats.isNearLimit) {
          console.warn('Memory optimization: Cache usage near limit', stats);
        }
      };

      const memoryTimer = setInterval(logMemoryStats, 10000); // Log every 10 seconds

      return () => clearInterval(memoryTimer);
    }
  }, [getMemoryStats]);

  return {
    data,
    setData: setOptimizedData,
    getCached,
    setCached,
    preloadData,
    clearCache,
    forceCleanup,
    getMemoryStats,
  };
};